import { SearchIcon, XIcon } from "lucide-react";
import { ProductList } from "./ProductList";
import { cn } from "@/lib/utils";
import { Input } from "./ui/input";
import { MenuItem } from "@/hooks/useMenu";

import { menuRoute } from "@/App";
import { useState, useEffect } from "react";
import { uniq, uniqBy } from "lodash-es";
import { useLocationMenus } from "@/hooks/useLocationMenus";

function SearchDrawer() {
  const [searchQuery, setSearchQuery] = useState("");
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setViewportHeight(vv.height);
    update();
    vv.addEventListener("resize", update);
    return () => vv.removeEventListener("resize", update);
  }, []);
  const { menuId } = menuRoute.useParams();
  const { availableMenus: menus } = useLocationMenus();

  const hasSearched = searchQuery !== "";

  function handleSearchInput(event: React.ChangeEvent<HTMLInputElement>) {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
  }

  function filterProducts(products: MenuItem[]) {
    if (hasSearched) {
      return products.filter(
        (product) =>
          product.name.toLowerCase().includes(searchQuery) ||
          product.description.toLowerCase().includes(searchQuery)
      );
    }

    return [];
  }

  const allCategories = uniqBy(
    menus
      .filter((menu) => menu.id === menuId)
      .map((menu) => menu.categories)
      .flat(),
    "id"
  );

  const searchResultCount = uniq(
    allCategories.map((category) => filterProducts(category.products)).flat()
  ).length;

  const hasSearchResults = searchResultCount > 0;

  return (
    <div
      className="bg-background text-foreground rounded-t-xl md:rounded-xl w-full overflow-hidden flex flex-col"
      style={{ maxHeight: viewportHeight ? `${viewportHeight * 0.92}px` : '85dvh' }}
    >
      <header className="flex-shrink-0 flex flex-col gap-y-3 py-4 border-b px-4">
        <div className="flex w-full relative text-foreground">
          <Input
            autoFocus
            type="search"
            placeholder={`Search menu...`}
            className="text-foreground-muted w-full border-none bg-muted rounded-full px-4 h-11 text-[16px] pr-4"
            onChange={(event) => handleSearchInput(event)}
            value={searchQuery}
          />
          <button
            type="button"
            disabled={searchQuery === ""}
            onClick={() => {
              setSearchQuery("");
            }}
          >
            {searchQuery === "" ? (
              <SearchIcon
                className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none opacity-50"
                size={14}
              />
            ) : (
              <XIcon
                size={14}
                className="absolute right-4 top-1/2 -translate-y-1/2 opacity-50"
              />
            )}
          </button>
        </div>

        <p
          className={cn(
            "text-muted-foreground text-sm transition",
            hasSearched
              ? "opacity-100"
              : "opacity-0 select-none pointer-events-none"
          )}
        >
          {searchResultCount} results
        </p>
      </header>

      <main className="flex-1 min-h-0 overflow-y-auto pb-[100px]">
        {hasSearched ? (
          <>
            {hasSearchResults ? (
              allCategories.map((category) => {
                const hasProducts =
                  filterProducts(category.products)?.length > 0;
                if (!hasProducts) return null;

                return (
                  <div key={category.name} className="border-solid mb-3">
                    <div className="flex justify-between items-center p-4 pb-0">
                      <h2 className="text-xl tracking-tighter">
                        {category.name}
                      </h2>
                    </div>

                    <ProductList
                      products={uniq(filterProducts(category.products))}
                    />
                  </div>
                );
              })
            ) : (
              <p className="px-4 text-muted-foreground text-center py-12">
                No results for {searchQuery} - try searching for something else
              </p>
            )}
          </>
        ) : (
          <p className="px-4 text-muted-foreground text-center py-12">
            Search for something to get started
          </p>
        )}
      </main>
    </div>
  );
}

export { SearchDrawer };
